import { useState } from "react";
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  FormHelperText,
  Paper,
  Snackbar,
  Alert,
  CircularProgress
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { zoullooColors } from "../../theme";
import { useCreateUserAuth } from "../../hooks/useUser";
import { CreateInternalUserRequest } from "../../types/auth";
import { UserRole } from "../../models/UserRole";
import { ROLE_OPTIONS } from "../../constants/roles";
import { useTranslation } from "react-i18next";

const Users: React.FC = () => {
  const { t } = useTranslation();
  
  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  // 4 Calendly/Cal.com booking URLs surfaced in Account Management emails.
  const [schedulingUrlClientFr, setSchedulingUrlClientFr] = useState("");
  const [schedulingUrlClientEn, setSchedulingUrlClientEn] = useState("");
  const [schedulingUrlTalentFr, setSchedulingUrlTalentFr] = useState("");
  const [schedulingUrlTalentEn, setSchedulingUrlTalentEn] = useState("");
  
  // Validation state
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    userType: false
  });
  
  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error">("success");
  
  const createUserMutation = useCreateUserAuth();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors = {
      firstName: firstName.trim() === "",
      lastName: lastName.trim() === "",
      email: email.trim() === "" || !email.includes("@"),
      userType: userType === ""
    };
    
    setErrors(newErrors);
    
    if (Object.values(newErrors).some(error => error)) {
      return;
    }
    
    const selectedRole = ROLE_OPTIONS.find(option => option.value === userType)?.role || UserRole.CSM;
    
    const userData: CreateInternalUserRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        roles: [selectedRole],
        phoneNumber: phoneNumber.trim() || undefined,
        schedulingUrlClientFr: schedulingUrlClientFr.trim() || undefined,
        schedulingUrlClientEn: schedulingUrlClientEn.trim() || undefined,
        schedulingUrlTalentFr: schedulingUrlTalentFr.trim() || undefined,
        schedulingUrlTalentEn: schedulingUrlTalentEn.trim() || undefined,
      };
    
    createUserMutation.mutate(userData, {
      onSuccess: () => {
        // Show success message
        setAlertSeverity("success");
        setAlertMessage(t('admin.userCreated'));
        setAlertOpen(true);
        
        // Reset form
        setFirstName("");
        setLastName("");
        setEmail("");
        setUserType("");
        setPhoneNumber("");
        setSchedulingUrlClientFr("");
        setSchedulingUrlClientEn("");
        setSchedulingUrlTalentFr("");
        setSchedulingUrlTalentEn("");
      },
      onError: (error) => {
        console.error("Error creating user:", error);
        
        // Show error message
        setAlertSeverity("error");
        setAlertMessage(t('admin.userCreationError'));
        setAlertOpen(true);
      }
    });
  };
  
  const handleAlertClose = () => {
    setAlertOpen(false);
  };
  
  return (
   <Box px={6} py={4}>
      <Typography variant="h2" mb={4}>{t('admin.adminPanel')}</Typography>
      
      <Paper elevation={3} sx={{ p: 4, maxWidth: 800 }}>
        <Typography variant="h4" mb={3}>{t('admin.createUser')}</Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('admin.firstName')}
                fullWidth
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={errors.firstName}
                helperText={errors.firstName ? t('admin.requiredField') : ""}
                required
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('admin.lastName')}
                fullWidth
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={errors.lastName}
                helperText={errors.lastName ? t('admin.requiredField') : ""}
                required
              />
            </Grid>
            
            <Grid size={12}>
              <TextField
                label={t('admin.email')}
                fullWidth
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                helperText={errors.email ? t('admin.invalidEmail') : ""}
                required
              />
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth error={errors.userType} required>
                <InputLabel id="user-type-label">{t('admin.userType')}</InputLabel>
                <Select
                  labelId="user-type-label"
                  value={userType}
                  label={t('admin.userType')}
                  onChange={(e) => setUserType(e.target.value)}
                >
                  {ROLE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.userType && <FormHelperText>{t('admin.selectUserType')}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Phone number is surfaced in Account Management emails as the
                "Votre contact dédié" block — only relevant for CSM/TAM, not
                shown until a userType is picked. */}
            {(userType === UserRole.CSM || userType === UserRole.TAM) && (
              <Grid size={12}>
                <TextField
                  label={t('admin.phoneNumber', 'Téléphone')}
                  fullWidth
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+33 6 12 34 56 78"
                  helperText={t(
                    'admin.phoneNumberHelp',
                    'Optionnel — affiché dans les emails Account Management comme contact dédié.'
                  )}
                />
              </Grid>
            )}

            {/* URLs de prise de rendez-vous — affichées uniquement pour les CSM
                (pas les TAM). Surfacées comme {{schedulingUrl}} dans les emails AM. */}
            {userType === UserRole.CSM && (
              <>
                <Grid size={12}>
                  <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600, color: "text.secondary" }}>
                    {t('admin.schedulingUrlsTitle', 'URLs de prise de rendez-vous')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(
                      'admin.schedulingUrlsHelp',
                      'Optionnelles — affichées dans les emails Account Management envoyés par ce CSM. La bonne URL est choisie automatiquement selon le destinataire (Client ou Talent) et sa langue.'
                    )}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label={t('admin.schedulingUrlClientFr', 'URL Client — FR')}
                    value={schedulingUrlClientFr}
                    onChange={(e) => setSchedulingUrlClientFr(e.target.value)}
                    placeholder="https://..."
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label={t('admin.schedulingUrlClientEn', 'URL Client — EN')}
                    value={schedulingUrlClientEn}
                    onChange={(e) => setSchedulingUrlClientEn(e.target.value)}
                    placeholder="https://..."
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label={t('admin.schedulingUrlTalentFr', 'URL Talent — FR')}
                    value={schedulingUrlTalentFr}
                    onChange={(e) => setSchedulingUrlTalentFr(e.target.value)}
                    placeholder="https://..."
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label={t('admin.schedulingUrlTalentEn', 'URL Talent — EN')}
                    value={schedulingUrlTalentEn}
                    onChange={(e) => setSchedulingUrlTalentEn(e.target.value)}
                    placeholder="https://..."
                  />
                </Grid>
              </>
            )}

            <Grid size={12}>
              <Button
                type="submit" 
                variant="contained" 
                disabled={createUserMutation.isPending}
                sx={{ 
                  mt: 2, 
                  backgroundColor: zoullooColors.secondary.main,
                  color: zoullooColors.secondary.contrastText,
                  "&:hover": {
                    backgroundColor: zoullooColors.secondary.dark,
                  }
                }}
              >
                {createUserMutation.isPending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('admin.createUserBtn')
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      <Snackbar open={alertOpen} autoHideDuration={6000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity={alertSeverity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;